import { OMS_COLORS, RESOURCE_STATUS_SEGMENTS, tint } from '../omsDashTheme';
import type { OmsResourceStatusCounts } from '../types';

/** Defensive fallback for payloads cached before statusCounts existed. */
export function statusCountsOf(row: {
  statusCounts?: OmsResourceStatusCounts;
  totalCompleted?: number;
  totalPending?: number;
  passTasks?: number;
  failTasks?: number;
  pendingTasks?: number;
}): OmsResourceStatusCounts {
  if (row.statusCounts) return row.statusCounts;
  return {
    pass: row.passTasks ?? row.totalCompleted ?? 0,
    fail: row.failTasks ?? 0,
    inprogress: 0,
    due: row.pendingTasks ?? row.totalPending ?? 0,
    overdue: 0,
    hold: 0,
  };
}

export const totalOfStatusCounts = (c: OmsResourceStatusCounts) =>
  c.pass + c.fail + c.inprogress + c.due + c.overdue + c.hold;

/** Stacked status bar built from divs so every segment keeps its exact hue. */
export function StatusBar({ counts, height = 10 }: { counts: OmsResourceStatusCounts; height?: number }) {
  const total = Math.max(totalOfStatusCounts(counts), 1);
  return (
    <div
      className="flex w-full overflow-hidden rounded-full"
      style={{ height, backgroundColor: 'rgba(11,59,46,0.08)' }}
    >
      {RESOURCE_STATUS_SEGMENTS.map((seg) =>
        counts[seg.key] > 0 ? (
          <div
            key={seg.key}
            title={`${seg.label}: ${counts[seg.key]}`}
            style={{ width: `${(counts[seg.key] / total) * 100}%`, backgroundColor: seg.color }}
          />
        ) : null
      )}
    </div>
  );
}

/** Per-status count chips; zero-count statuses stay visible but dimmed. */
export function StatusLegendChips({ counts }: { counts: OmsResourceStatusCounts }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {RESOURCE_STATUS_SEGMENTS.map((seg) => (
        <span
          key={seg.key}
          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums"
          style={{
            color: seg.color,
            backgroundColor: tint(seg.color, 0.10),
            opacity: counts[seg.key] > 0 ? 1 : 0.45,
          }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: seg.color }} />
          {seg.label} {counts[seg.key]}
        </span>
      ))}
    </div>
  );
}

/** Compact color key (no counts) for the top of the engineer list. */
export function StatusColorKey() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {RESOURCE_STATUS_SEGMENTS.map((seg) => (
        <span key={seg.key} className="flex items-center gap-1 text-[10px] font-bold" style={{ color: OMS_COLORS.caption }}>
          <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: seg.color }} />
          {seg.label}
        </span>
      ))}
    </div>
  );
}
